import { Controller } from "@hotwired/stimulus"

/**
 * Policy Map Controller
 *
 * Drives the interactive U.S. menopause policy map: colors states by status,
 * shows a hover tooltip, renders a detail panel on click/keyboard select, and
 * filters the map by legislative lane or status.
 */
export default class extends Controller {
  static targets = ["state", "tooltip", "panel", "panelEmpty", "panelBody", "filter", "legend"]
  static values = { states: Object }

  connect() {
    this.activeLane = "all"
    this.hiddenStatuses = new Set()
    this.selectedCode = null
    this.paintStates()
  }

  paintStates() {
    this.stateTargets.forEach((path) => {
      const data = this.dataFor(path)
      const status = data ? data.status : "none"
      path.dataset.status = status
      path.dataset.lanes = data ? data.lanes.join(" ") : ""
      path.classList.add("policy-map-state")
      if (data) {
        path.classList.add("is-tracked")
      }
      this.applyVisibility(path)
    })
  }

  dataFor(path) {
    const code = path.dataset.state
    const entry = this.statesValue[code]
    return entry && entry.entries && entry.entries.length ? entry : null
  }

  applyVisibility(path) {
    const status = path.dataset.status
    const lanes = (path.dataset.lanes || "").split(" ").filter(Boolean)

    const statusHidden = status !== "none" && this.hiddenStatuses.has(status)
    const laneHidden = this.activeLane !== "all" && !lanes.includes(this.activeLane)

    path.classList.toggle("is-dimmed", statusHidden || laneHidden)
  }

  hover(event) {
    const path = event.currentTarget
    const data = this.statesValue[path.dataset.state]
    if (!data) return
    const label = data.entries.length
      ? `${data.name} — ${data.statusLabel}`
      : `${data.name} — No tracked activity`
    this.tooltipTarget.textContent = label
    this.tooltipTarget.hidden = false
    this.move(event)
  }

  move(event) {
    if (this.tooltipTarget.hidden) return
    const rect = this.element.querySelector(".policy-map-canvas").getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    this.tooltipTarget.style.left = `${x}px`
    this.tooltipTarget.style.top = `${y}px`
  }

  leave() {
    this.tooltipTarget.hidden = true
  }

  keySelect(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.select(event)
    }
  }

  select(event) {
    const path = event.currentTarget
    const code = path.dataset.state
    const data = this.statesValue[code]

    this.stateTargets.forEach((p) => p.classList.remove("is-selected"))
    path.classList.add("is-selected")
    this.selectedCode = code

    this.renderPanel(data)
  }

  renderPanel(data) {
    this.panelEmptyTarget.hidden = true
    this.panelBodyTarget.hidden = false

    if (!data || !data.entries.length) {
      this.panelBodyTarget.innerHTML = `
        <header class="policy-map-panel-header">
          <h3>${this.escape(data ? data.name : "Unknown")}</h3>
          <span class="policy-map-badge policy-map-badge--none">No tracked activity</span>
        </header>
        <p class="policy-map-panel-note">No menopause-related legislation is tracked for this state in the current brief.</p>
      `
      return
    }

    const entriesHtml = data.entries.map((entry) => this.entryHtml(entry)).join("")
    this.panelBodyTarget.innerHTML = `
      <header class="policy-map-panel-header">
        <h3>${this.escape(data.name)}</h3>
        <span class="policy-map-badge policy-map-badge--${data.status}">${this.escape(data.statusLabel)}</span>
      </header>
      <div class="policy-map-entries">${entriesHtml}</div>
    `
  }

  entryHtml(entry) {
    const lanes = entry.laneLabels
      .map((label) => `<span class="policy-map-lane-chip">${this.escape(label)}</span>`)
      .join("")
    const source = entry.sourceUrl
      ? `<a href="${this.escape(entry.sourceUrl)}" target="_blank" rel="noopener">${this.escape(entry.source)}</a>`
      : `<span>${this.escape(entry.source)}</span>`

    return `
      <article class="policy-map-entry policy-map-entry--${entry.status}">
        <div class="policy-map-entry-top">
          <span class="policy-map-badge policy-map-badge--${entry.status}">${this.escape(entry.statusLabel)}</span>
          <div class="policy-map-lane-chips">${lanes}</div>
        </div>
        <h4 class="policy-map-entry-title">${this.escape(entry.title)}</h4>
        <p class="policy-map-entry-effective">${this.escape(entry.effective)}</p>
        <p class="policy-map-entry-covers">${this.escape(entry.covers)}</p>
        <p class="policy-map-entry-rationale"><strong>Why:</strong> ${this.escape(entry.rationale)}</p>
        <p class="policy-map-entry-source">Source: ${source}</p>
      </article>
    `
  }

  filterLane(event) {
    const button = event.currentTarget
    this.activeLane = button.dataset.lane
    this.filterTargets.forEach((b) => b.classList.toggle("is-active", b === button))
    this.stateTargets.forEach((path) => this.applyVisibility(path))
  }

  toggleStatus(event) {
    const button = event.currentTarget
    const status = button.dataset.status
    if (this.hiddenStatuses.has(status)) {
      this.hiddenStatuses.delete(status)
      button.classList.remove("is-off")
    } else {
      this.hiddenStatuses.add(status)
      button.classList.add("is-off")
    }
    this.stateTargets.forEach((path) => this.applyVisibility(path))
  }

  escape(value) {
    if (value == null) return ""
    const div = document.createElement("div")
    div.textContent = String(value)
    return div.innerHTML
  }
}
